import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";

import Button from "@/components/atoms/Buttons/Button";
import i18n from "@/translate/i18n";
import { Title } from "@/components/atoms/Typography/styles";
import { ReactComponent as ErrorIcon } from "@/assets/images/error.svg";
import { ReactComponent as RestrictedIcon } from "@/assets/images/restricted.svg";
import { ReactComponent as ExpiredIcon } from "@/assets/images/red_clock.svg";
import { LayoutWrapper } from "@/components/molecules/Wrappers/LayoutWrapper";
import { useTransactionData } from "@/context/transactionContext";

const TIME_TO_REDIRECT = 7000;

const ERROR_TYPES = {
  DOCUMENT_REFUSED: "document-refused",
  REACHED_ATTEMPTS: "reached-attempts",
};

const isValidUrl = (urlString) => {
  if (!urlString) return false;
  try {
    return Boolean(new URL(urlString));
  } catch (e) {
    return false;
  }
};

function ErrorMessage({
  isExpired = false,
  handleClick,
  errorType,
  transactionData: dataFromProps,
}) {
  const { transactionData: dataFromContext } = useTransactionData();
  const transactionData = dataFromProps || dataFromContext;

  const hasValidFailedUrl = useMemo(
    () => isValidUrl(transactionData?.redirect_url_failed),
    [transactionData?.redirect_url_failed],
  );

  useEffect(() => {
    if (!isExpired || !hasValidFailedUrl) return undefined;

    const timer = setTimeout(() => {
      window.location.href = transactionData.redirect_url_failed;
    }, TIME_TO_REDIRECT);

    return () => clearTimeout(timer);
  }, [isExpired, hasValidFailedUrl, transactionData]);

  const isRestricted =
    errorType === ERROR_TYPES.DOCUMENT_REFUSED ||
    errorType === ERROR_TYPES.REACHED_ATTEMPTS;

  const showBackLink = !isExpired && !isRestricted && hasValidFailedUrl;

  const showRetryButton = !isExpired && !isRestricted;

  const renderErrorContent = () => {
    if (isExpired) {
      return (
        <>
          <ExpiredIcon className="w-14 mt-8" />
          <div className="mt-8">
            <Title>
              {transactionData?.type === "deposit"
                ? i18n.t("error.expiredTitle")
                : i18n.t("error.expiredTitleWithdrawal")}
            </Title>
          </div>
          <h3 className="mt-5 font-medium text-md text-black max-w-72">
            {i18n.t("error.expiredSubtitle")}
          </h3>
          <p className="mt-5 mb-6 text-sm max-w-72">
            {i18n.t("error.expiredInfo")}
          </p>
        </>
      );
    }

    if (isRestricted) {
      return (
        <>
          <RestrictedIcon className="w-14 mt-8" />
          <div className="mt-8">
            <Title>{i18n.t("error.restricted")}</Title>
          </div>
          <h3 className="mt-5 font-medium text-md text-black max-w-72">
            {i18n.t(
              errorType === ERROR_TYPES.DOCUMENT_REFUSED
                ? "error.restrictedInfo"
                : "error.attemptsInfo",
            )}
          </h3>
          <p className="mt-5 mb-6 text-xs">{i18n.t("error.contactUs")}</p>
        </>
      );
    }

    return (
      <>
        <ErrorIcon className="w-14 mt-8" />
        <div className="mt-8">
          <Title>{i18n.t("error.title")}</Title>
        </div>
        <h3 className="mt-5 font-medium text-md text-black max-w-72">
          {i18n.t("error.subtitle")}
        </h3>
        <p className="mt-5 mb-6 text-sm max-w-72">{i18n.t("error.info")}</p>
      </>
    );
  };

  return (
    <LayoutWrapper className="!justify-center">
      <div className="flex flex-col items-center">{renderErrorContent()}</div>

      {showBackLink && (
        <a
          className="mt-10 text-cripten-blue underline cursor-pointer"
          href={transactionData?.redirect_url_failed}
        >
          {i18n.t("general.backTo", {
            merchant: transactionData?.merchant?.name,
          })}
        </a>
      )}

      <div className="mb-14">
        {showRetryButton && (
          <Button type="button" variant="secondary" handleClick={handleClick}>
            {/* {i18n.t('form.retry')} */}
          </Button>
        )}
      </div>
    </LayoutWrapper>
  );
}

ErrorMessage.propTypes = {
  handleClick: PropTypes.func,
  isExpired: PropTypes.bool,
  errorType: PropTypes.string,
  transactionData: PropTypes.shape({
    type: PropTypes.string,
    redirect_url_failed: PropTypes.string,
    merchant: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
};

ErrorMessage.defaultProps = {
  handleClick: () => {},
  errorType: "",
  transactionData: null,
};

export default ErrorMessage;
